import React, { useEffect, useRef } from 'react';
import { Toolbar } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Utils
import {
  SCREEN_PARAMETER,
  isEmptyLoadingParametersScreen,
  isValidAllParametersScreen
} from './utilsLayoutMain';
import { getParameterByTypeScreen } from '@/main/store/parameterByTypeScreen';
import {
  getKeyRouteConstantByRoute,
  getRouteWithoutParams,
  getScreensWithoutModule,
  isEmpty
} from '@/main/services/utils';
import { RoutesConstants } from '../../routes/routesConstants';
import { useParameterByTypeScreenAllReducer } from '@/main/store/parameterByTypeScreen/parameterByTypeScreenHooks';

// Redux
import { MenuItemResponse } from '@/main/models/response/menu';
import {
  getControlsByScreen,
  resetReducerControlsByScreen,
  useControlReducer
} from '@/main/store/control';
import {
  useMenuReducer,
  setMenuItemSelected,
  setOpenMenu
} from '../../store/menu';
import {
  changeDragDropScreen,
  resetReducerDragDropScreen,
  useDragDropScreenReducer
} from '@/main/store/dragDropScreen';

// Components
import DrawerMenuComponent from '../menu/drawerMenu/drawerMenuComponent';
import ModalAnnouncements from "../modalAnnouncements/modalAnnouncements";
import LoadingComponent from '../shared/loading/loadingComponent';
import AcceptanceTermsModal from '../shared/acceptanceTermsModal/acceptanceTermsModal';
import ErrorBoundaryScreen from '../errorsBoundary/errorBoundaryScreen';
import ModalUserChangePasswordAlert from '@/main/screens/login/components/modalUserChangePasswordAlert/modalUserChangePasswordAlert';
import HubContainer from '../shared/hub/HubContainer';
import { SendUpdatesSignalRTypeEnum } from '@/main/services/enum/SendUpdatesSignalRTypeEnum';
import { useAccessErrorReducer } from '@/main/store/accessError/accessErrorHooks';
import { clearAccessError } from '@/main/store/accessError';
import NoPermission from '@/main/screens/noPermission/noPermission';

interface LayoutMainProps {
  children: React.ReactNode;
}

const LayoutMain = ({ children }: LayoutMainProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Reducers
  const menuReducer = useMenuReducer();
  const controlReducer = useControlReducer();
  const parametersByTypeScreenReducerAll = useParameterByTypeScreenAllReducer();
  const { dragDropScreen: dragDropScreenReducer } = useDragDropScreenReducer();
  const accessErrorReducer = useAccessErrorReducer();

  // Constants
  const refControlReducer = useRef<boolean | null>(null);
  const keyRoute = getKeyRouteConstantByRoute(pathname);
  const parametersRequiredScreen = SCREEN_PARAMETER[keyRoute] ?? [];
  const isLoadingRequiredScreen = isEmptyLoadingParametersScreen(
    parametersByTypeScreenReducerAll,
    parametersRequiredScreen
  );

  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const haveFiles = e.dataTransfer.types.includes('Files');

    if (dragDropScreenReducer.data || !haveFiles) return;
    dispatch(changeDragDropScreen(true));
  }

  function checkParametersScreen() {
    const isValidParameters = isValidAllParametersScreen(
      parametersByTypeScreenReducerAll,
      parametersRequiredScreen
    );
    if (isEmpty(parametersRequiredScreen) || isValidParameters) return;
    parametersRequiredScreen.forEach(item => {
      dispatch(getParameterByTypeScreen(item));
    });
  }

  const handleDrawerClose = () => {
    if (menuReducer.open) {
      dispatch(setOpenMenu(false));
    }
  };

  // Render functions
  const ignoredRoutes = () =>
    pathname !== RoutesConstants.home && pathname !== RoutesConstants.notFound;

  function renderChildren() {
    if (
      controlReducer.controlsByScreen.isLoading &&
      ignoredRoutes() &&
      !refControlReducer.current
    ) {
      refControlReducer.current = null;
      return <LoadingComponent />;
    }
    if (isLoadingRequiredScreen) return <LoadingComponent />;

    return (
      <>
        {children}

        <ModalUserChangePasswordAlert />
      </>
    );
  }

  useEffect(() => {
    return () => {
      dispatch(resetReducerDragDropScreen());
    };
  }, []);

  useEffect(() => {
    if (!menuReducer.menu.isLoading && menuReducer.menu.data !== null) {
      // Validate access route
      const screens = getScreensWithoutModule();

      if (screens.length > 0 && ignoredRoutes()) {
        const screen: MenuItemResponse | undefined = screens.find(
          (x: MenuItemResponse) => getRouteWithoutParams(x.route) === pathname
        );
        if (!screen) {
          navigate(RoutesConstants.notFound);
        } else {
          // Get controls of screen
          refControlReducer.current = true;
          dispatch(getControlsByScreen(screen.guid));
        }
      }
    }
    dispatch(setMenuItemSelected(pathname));

    if (!ignoredRoutes() && controlReducer.controlsByScreen.data !== null) {
      // Reset controls of screen
      dispatch(resetReducerControlsByScreen());
    }
  }, [dispatch, pathname, menuReducer.menu.isLoading]);

  useEffect(() => {
    checkParametersScreen();
  }, [pathname]);

  useEffect(() => {
    dispatch(clearAccessError());
  }, [dispatch, pathname]);

  return (
    <>
      <DrawerMenuComponent
        data={
          menuReducer.menu.data && menuReducer.menu.data.length > 0
            ? menuReducer.menu.data
            : []
        }
      />
      <div
        id="content-main"
        onClick={handleDrawerClose}
        onDragEnter={handleDragEnter}
        onDragLeave={() => dispatch(changeDragDropScreen(false))}>
        <Toolbar style={{ minHeight: 70 }} />
        <ErrorBoundaryScreen route={pathname}>
          {accessErrorReducer.hasAccessError ? (
            <NoPermission />
          ) : (
            renderChildren()
          )}
          <HubContainer type={SendUpdatesSignalRTypeEnum.ExportInventory} />
          <HubContainer type={SendUpdatesSignalRTypeEnum.ImportPDFQuote} />

          <HubContainer type={SendUpdatesSignalRTypeEnum.DynamicReport} />

          <HubContainer type={SendUpdatesSignalRTypeEnum.RateImport} />
        </ErrorBoundaryScreen>
      </div>
			<ModalAnnouncements />
      <AcceptanceTermsModal />
    </>
  );
};

export default LayoutMain;
